import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { format } from 'date-fns';
import { FileText, Users, CheckCircle, Clock, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchInquiries = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get(`${API}/inquiries`, { params });
      setInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      await axios.patch(`${API}/inquiry/${inquiryId}`, { status: newStatus });
      fetchInquiries();
    } catch (error) {
      console.error('Error updating inquiry:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-primary/10 text-primary border-primary/30',
      in_discussion: 'bg-inquiry/10 text-inquiry border-inquiry/30',
      proposal_sent: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      confirmed: 'bg-green-500/10 text-green-400 border-green-500/30',
      declined: 'bg-red-500/10 text-red-400 border-red-500/30',
      cancelled: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
    };
    return colors[status] || colors.new;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <FileText size={16} />;
      case 'confirmed':
        return <CheckCircle size={16} />;
      case 'in_discussion':
      case 'proposal_sent':
        return <Clock size={16} />;
      case 'declined':
      case 'cancelled':
        return <XCircle size={16} />;
      default:
        return <FileText size={16} />;
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-32 pb-24">
      <div className="px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-4">
            Residency Inquiries
          </h1>
          <p className="text-lg text-text-secondary">Manage and track all residency requests</p>
        </motion.div>

        {/* Filters */}
        <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
          {['all', 'new', 'in_discussion', 'proposal_sent', 'confirmed', 'declined'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-6 py-2 rounded-sm border transition-all duration-300 text-sm font-medium uppercase tracking-wide whitespace-nowrap ${
                filter === status
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-white/10 text-text-secondary hover:border-white/20'
              }`}
            >
              {status.replace('_', ' ')}
            </button>
          ))}
        </div>

        {/* Inquiries List */}
        {loading ? (
          <div className="text-center py-20 text-text-secondary">Loading inquiries...</div>
        ) : inquiries.length === 0 ? (
          <div className="text-center py-20 text-text-secondary">No inquiries found</div>
        ) : (
          <div className="space-y-4">
            {inquiries.map((inquiry) => (
              <motion.div
                key={inquiry.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-background-surface border border-white/5 hover:border-primary/30 transition-all duration-300 rounded-sm overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-semibold text-foreground mb-1">
                        {inquiry.first_name} {inquiry.last_name}
                      </h3>
                      {inquiry.entity_label && (
                        <p className="text-sm text-text-secondary mb-2">{inquiry.entity_label}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-sm text-text-muted">
                        <span>{inquiry.email}</span>
                        <span>•</span>
                        <span>{inquiry.phone}</span>
                      </div>
                    </div>
                    <div className={`px-4 py-2 rounded-sm border text-xs uppercase tracking-wide font-semibold flex items-center gap-2 ${getStatusColor(inquiry.status)}`}>
                      {getStatusIcon(inquiry.status)}
                      {inquiry.status.replace('_', ' ')}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 pt-4 border-t border-white/5">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-text-muted mb-1">Project</p>
                      <p className="text-foreground font-medium">{inquiry.project_type.replace('_', ' ').toUpperCase()}</p>
                      <p className="text-sm text-text-secondary">{inquiry.num_people} people</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-text-muted mb-1">Duration</p>
                      <p className="text-foreground font-medium">{inquiry.estimated_duration}</p>
                      {inquiry.flexible_dates && <p className="text-sm text-text-secondary">Flexible dates</p>}
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-text-muted mb-1">Dates</p>
                      {inquiry.preferred_start_date && inquiry.preferred_end_date ? (
                        <p className="text-foreground text-sm">
                          {new Date(inquiry.preferred_start_date).toLocaleDateString()} → {new Date(inquiry.preferred_end_date).toLocaleDateString()}
                        </p>
                      ) : (
                        <p className="text-text-secondary text-sm">Not specified</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="text-sm">
                      <span className="text-text-muted">Accommodation:</span>
                      <span className="text-foreground ml-2">{inquiry.accommodation_needed === 'full_house' ? 'Full House' : 'Studio Only'}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-text-muted">Engineer:</span>
                      <span className="text-foreground ml-2">{inquiry.need_engineer?.replace('_', ' ') || 'N/A'}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-text-muted">Transport:</span>
                      <span className="text-foreground ml-2">{inquiry.transport_needed ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-text-muted">Catering:</span>
                      <span className="text-foreground ml-2">{inquiry.catering_interest ? 'Yes' : 'No'}</span>
                    </div>
                  </div>

                  {inquiry.message && (
                    <div className="mb-4 p-4 bg-background rounded-sm">
                      <p className="text-xs uppercase tracking-widest text-text-muted mb-2">Message</p>
                      <p className="text-sm text-text-secondary leading-relaxed">{inquiry.message}</p>
                    </div>
                  )}

                  <div className="flex gap-2 pt-4 border-t border-white/5">
                    {['new', 'in_discussion', 'proposal_sent', 'confirmed', 'declined'].map((status) => (
                      inquiry.status !== status && (
                        <button
                          key={status}
                          onClick={() => updateInquiryStatus(inquiry.id, status)}
                          className="px-4 py-2 text-xs uppercase tracking-wide border border-white/10 hover:border-primary hover:text-primary transition-colors rounded-sm text-text-secondary"
                        >
                          {status === 'in_discussion' ? 'Discuss' : status === 'proposal_sent' ? 'Proposal' : status}
                        </button>
                      )
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;